/**
 * The overview's weeks and months of life, worded for the phone. @babia/checks
 * counts the days (`countedDays`, `periodRows`); this file names each period
 * as an age, words its numbers in the household's unit, compares it with the
 * period before, and lays it out as chart rows and table cells. The words
 * describe what was logged and never judge an amount. Pure, like
 * `overview.ts`.
 */
import {
  ageInMonths,
  amountText,
  type BabyPeriods,
  type BottlePeriod,
  type BottleUnit,
  type ChangePeriod,
  fromOz,
  localCalendar,
  monthOfLifeStart,
  type Period,
  PERIODS_SHOWN,
  type PeriodUnit,
  roundAmount,
  type SleepPeriod,
} from "@babia/checks";
import { niceMax } from "./overview.ts";
import { pluralize, shortDuration } from "./time.ts";

/** What one bar of the Trends charts covers: a day, a week, or a month. */
export type Zoom = "days" | "weeks" | "months";

export const ZOOM_UNIT = { weeks: "week", months: "month" } as const satisfies Record<
  Exclude<Zoom, "days">,
  PeriodUnit
>;

/** The kinds the Trends tabs switch between. */
export type TrendKind = "bottles" | "changes" | "sleep";

/**
 * Local midnight a day before the oldest month any baby's chart can show, so
 * one read covers Weeks and Months alike (twelve months reach further back
 * than 26 weeks) and a sleep from the evening before reaches the first night.
 * Null when no baby has a birth date on or before `today`: nothing to fetch.
 * `today` is a date (YYYY-MM-DD), so the read moves only at midnight or with a
 * birth date.
 */
export function seriesSince(
  babies: readonly { birthDate: string | null }[],
  today: string,
): Date | null {
  let oldest: string | null = null;
  for (const { birthDate } of babies) {
    if (birthDate === null || birthDate > today) continue;
    const first = Math.max(0, ageInMonths(birthDate, today) - PERIODS_SHOWN.month + 1);
    const start = monthOfLifeStart(birthDate, first);
    if (oldest === null || start < oldest) oldest = start;
  }
  if (oldest === null) return null;
  const [year, month, day] = oldest.split("-").map(Number);
  return localCalendar.dateStart(year!, month!, day! - 1);
}

/** A period's name, as an age: "This week so far", "First month", "6 weeks old". */
export function periodName(unit: PeriodUnit, p: Period): string {
  if (p.current) return `This ${unit} so far`;
  if (p.n === 0) return `First ${unit}`;
  return `${pluralize(p.n, unit, `${unit}s`)} old`;
}

/** One period as the chart, its caption, and its table show it. */
export interface PeriodRow {
  n: number;
  current: boolean;
  /** Counted days (nights, for sleep); none means no bar. */
  days: number;
  /** "6 weeks old" */
  name: string;
  /** The caption's first line after the name: "24.5 oz a day over 7 days". */
  text: string;
  /** The caption's second line: the change from the period before, then more numbers. */
  detail: string | null;
  /** The bar's parts by series key, in the unit shown; null draws nothing. */
  values: Record<string, number | null>;
  /** The table's cells after the period's name. */
  cells: string[];
}

export interface PeriodChart {
  /** The table's column heads after the period's. */
  heads: string[];
  rows: PeriodRow[];
}

const tenth = (n: number) => Math.round(n * 10) / 10;
const HOUR = 3_600_000;
const MINUTE = 60_000;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The caption's second line from its parts, or null with none. */
const detailOf = (parts: (string | null)[]) => {
  const kept = parts.filter((p): p is string => p !== null);
  return kept.length ? capitalize(kept.join(" · ")) : null;
};

/** Why a period has no bar. */
function gapText(p: Period, nights = false): string {
  const full = nights ? "whole nights" : "full days";
  return p.current ? `no ${full} yet` : `no ${full} logged`;
}

/**
 * The change from the period before, on the numbers as shown: "2.1 oz more
 * than the week before", "0.5 fewer than the month before", "the same as the
 * week before".
 */
function changeText(diff: number, text: (n: number) => string, unit: PeriodUnit, less = "less") {
  if (diff === 0) return `the same as the ${unit} before`;
  return `${text(Math.abs(diff))} ${diff > 0 ? "more" : less} than the ${unit} before`;
}

function bottlePeriodRows(
  periods: BottlePeriod[],
  unit: PeriodUnit,
  bottles: BottleUnit,
): PeriodRow[] {
  const shown = (p: BottlePeriod | undefined) =>
    p && p.avgOz !== null ? fromOz(p.avgOz, bottles) : null;
  return periods.map((p, i) => {
    const amount = shown(p);
    const before = shown(periods[i - 1]);
    const perDay = p.days ? tenth(p.bottles / p.days) : null;
    return {
      n: p.n,
      current: p.current,
      days: p.days,
      name: periodName(unit, p),
      text:
        amount === null
          ? gapText(p)
          : `${amountText(amount, bottles)} a day over ${pluralize(p.days, "day", "days")}`,
      detail:
        amount === null
          ? null
          : detailOf([
              before === null
                ? null
                : changeText(
                    roundAmount(amount - before, bottles),
                    (n) => amountText(n, bottles),
                    unit,
                  ),
              perDay === null ? null : `${perDay} ${perDay === 1 ? "bottle" : "bottles"} a day`,
            ]),
      values: { avg: amount },
      cells: [
        amount === null ? "—" : String(amount),
        perDay === null ? "—" : String(perDay),
        String(p.days),
      ],
    };
  });
}

function changePeriodRows(periods: ChangePeriod[], unit: PeriodUnit): PeriodRow[] {
  const perDay = (p: ChangePeriod | undefined) => (p?.days ? tenth(p.changes / p.days) : null);
  return periods.map((p, i) => {
    const count = perDay(p);
    const before = perDay(periods[i - 1]);
    const a = (n: number) => (p.days ? n / p.days : 0);
    const wet = tenth(a(p.wet));
    const dirty = tenth(a(p.dirty));
    return {
      n: p.n,
      current: p.current,
      days: p.days,
      name: periodName(unit, p),
      text:
        count === null
          ? gapText(p)
          : `${count} ${count === 1 ? "change" : "changes"} a day over ${pluralize(p.days, "day", "days")}`,
      detail:
        count === null
          ? null
          : detailOf([
              before === null ? null : changeText(tenth(count - before), String, unit, "fewer"),
              `${wet} wet, ${dirty} dirty`,
            ]),
      // Stacked bottom to top as on the Days chart: wet only, both, dirty only.
      values: {
        wet: a(p.wet - p.both) || null,
        both: a(p.both) || null,
        dirty: a(p.dirty - p.both) || null,
      },
      cells:
        count === null
          ? ["—", "—", "—", String(p.days)]
          : [String(count), String(wet), String(dirty), String(p.days)],
    };
  });
}

function sleepPeriodRows(periods: SleepPeriod[], unit: PeriodUnit): PeriodRow[] {
  const minutes = (ms: number | null | undefined) =>
    ms === null || ms === undefined ? null : Math.round(ms / MINUTE);
  return periods.map((p, i) => {
    const asleep = p.avgMs;
    const before = minutes(periods[i - 1]?.avgMs);
    const longest = p.avgLongestMs;
    // The longest stretch is solid under the rest; a stretch that ran past the
    // night's noon can be longer than the night's share of it.
    const solid = asleep === null ? 0 : Math.min(longest ?? 0, asleep);
    return {
      n: p.n,
      current: p.current,
      days: p.days,
      name: periodName(unit, p),
      text:
        asleep === null
          ? gapText(p, true)
          : `${shortDuration(asleep)} a night over ${pluralize(p.days, "night", "nights")}`,
      detail:
        asleep === null
          ? null
          : detailOf([
              before === null
                ? null
                : changeText(minutes(asleep)! - before, (m) => shortDuration(m * MINUTE), unit),
              longest === null ? null : `longest ${shortDuration(longest)}`,
            ]),
      values:
        asleep === null
          ? { longest: null, rest: null }
          : {
              longest: longest === null ? null : solid / HOUR,
              rest: (asleep - solid) / HOUR || null,
            },
      cells: [
        asleep === null ? "—" : shortDuration(asleep),
        longest === null ? "—" : shortDuration(longest),
        String(p.days),
      ],
    };
  });
}

/** One baby's periods of one kind, worded in the household's unit. */
export function periodChart(
  kind: TrendKind,
  periods: BabyPeriods,
  unit: PeriodUnit,
  bottles: BottleUnit,
): PeriodChart {
  if (kind === "bottles")
    return {
      heads: [`${bottles} a day`, "Bottles a day", "Days"],
      rows: bottlePeriodRows(periods.bottles, unit, bottles),
    };
  if (kind === "changes")
    return {
      heads: ["Changes a day", "Wet", "Dirty", "Days"],
      rows: changePeriodRows(periods.changes, unit),
    };
  return {
    heads: ["Asleep a night", "Longest", "Nights"],
    rows: sleepPeriodRows(periods.sleep, unit),
  };
}

/**
 * A shared scale for every baby's chart: the tallest stacked bar, rounded up,
 * to a whole `step` when given (3 for hours of sleep, so the top line is a
 * labeled tick: 15h, not an unlabeled 16h).
 */
export function periodMax(charts: readonly PeriodChart[], step?: number): number {
  let max = 0;
  for (const { rows } of charts)
    for (const r of rows)
      max = Math.max(
        max,
        Object.values(r.values).reduce<number>((n, v) => n + (v ?? 0), 0),
      );
  return step ? Math.max(step, Math.ceil(max / step) * step) : niceMax(max);
}
